// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import {
  Box,
  Button,
  Column,
  FolderIcon,
  PlusIcon,
  Row,
  SearchInput,
  Select,
  Spinner,
  Text,
  TextInput,
} from "@hightouch/htui";

import {
  folderPathLabel,
  type MessageFolderNode,
  NO_FOLDER_VALUE,
} from "./saved-messages-utils";
import type { FolderPickerState } from "./use-message-folders";

// Above this many folders the list gets a search box.
const SEARCH_THRESHOLD = 6;

export const FolderPicker = ({
  folders,
  counts,
  state,
  onCreate,
  isCreatingFolder = false,
  showNoFolder = false,
  noFolderLabel = "No folder",
  allowNestedCreate = false,
  busyFolderId,
  leadingRows = [],
}: {
  folders: MessageFolderNode[];
  /** Direct per-folder message counts, keyed by folder id. */
  counts?: Map<string, number>;
  state: FolderPickerState;
  /** Creates a folder from the in-progress name and selects it. */
  onCreate: () => void;
  isCreatingFolder?: boolean;
  /** Render a "No folder" row that files into the unfiled bucket (bulk move). */
  showNoFolder?: boolean;
  /** The "No folder" row's label, such as "Library" where the unfiled bucket is the Library's top level. */
  noFolderLabel?: string;
  /** Offer a parent-folder picker when creating (bulk move supports nesting). */
  allowNestedCreate?: boolean;
  /**
   * While set, that folder's row shows a spinner and every other control is
   * disabled. An id with no row, such as a folder still being created, disables them all.
   */
  busyFolderId?: string;
  /** Places that aren't folders, listed first, such as the Library's Drafts. */
  leadingRows?: { id: string; label: string }[];
}) => {
  const {
    search,
    setSearch,
    selectedId,
    setSelectedId,
    isCreating,
    setIsCreating,
    newFolderName,
    setNewFolderName,
    newFolderParentId,
    setNewFolderParentId,
  } = state;

  const isBusy = busyFolderId !== undefined;
  const query = search.trim().toLowerCase();
  const visibleFolders = query
    ? folders.filter((f) => f.name.toLowerCase().includes(query))
    : folders;

  const cancelCreate = () => {
    setIsCreating(false);
    setNewFolderName("");
    setNewFolderParentId(null);
  };

  const parentOptions = [
    { id: NO_FOLDER_VALUE, name: "No parent (top level)" },
    ...folders.map((f) => ({ id: f.id, name: folderPathLabel(f) })),
  ];

  return (
    <Column gap={2} minHeight={0}>
      {folders.length > SEARCH_THRESHOLD && (
        <SearchInput
          width="100%"
          placeholder="Find a folder…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}

      <Column gap={1} maxHeight="240px" minHeight={0} overflowY="auto">
        {!query &&
          leadingRows.map((row) => (
            <FolderRow
              key={row.id}
              label={row.label}
              isSelected={selectedId === row.id}
              busyFolderId={busyFolderId}
              id={row.id}
              onClick={() => setSelectedId(row.id)}
            />
          ))}
        {showNoFolder && !query && (
          <FolderRow
            label={noFolderLabel}
            isSelected={selectedId === NO_FOLDER_VALUE}
            busyFolderId={busyFolderId}
            id={NO_FOLDER_VALUE}
            onClick={() => setSelectedId(NO_FOLDER_VALUE)}
          />
        )}
        {visibleFolders.map((folder) => (
          <FolderRow
            key={folder.id}
            label={folderPathLabel(folder)}
            count={counts?.get(folder.id)}
            isSelected={selectedId === folder.id}
            busyFolderId={busyFolderId}
            id={folder.id}
            onClick={() => setSelectedId(folder.id)}
          />
        ))}
        {visibleFolders.length === 0 && (
          <Text color="text.secondary" size="sm">
            {query ? `No folders match “${search}”.` : "No folders yet."}
          </Text>
        )}
      </Column>

      {isCreating ? (
        <Column gap={2}>
          <Row gap={2} align="center">
            <TextInput
              placeholder="New folder name"
              value={newFolderName}
              isDisabled={isBusy}
              autoFocus
              width="100%"
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newFolderName.trim() && !isBusy)
                  onCreate();
                if (e.key === "Escape") cancelCreate();
              }}
            />
            <Button
              variant="secondary"
              isLoading={isCreatingFolder}
              isDisabled={
                !newFolderName.trim() || (isBusy && !isCreatingFolder)
              }
              onClick={onCreate}
            >
              Add
            </Button>
          </Row>
          {allowNestedCreate && (
            <Select
              // Inline, so picking a parent inside a popover isn't a click outside it.
              removePortal
              options={parentOptions}
              optionLabel={(option) => option.name}
              optionValue={(option) => option.id}
              value={newFolderParentId ?? NO_FOLDER_VALUE}
              width="100%"
              onChange={(value) =>
                setNewFolderParentId(
                  value === NO_FOLDER_VALUE ? null : (value ?? null),
                )
              }
            />
          )}
        </Column>
      ) : (
        <Box
          as="button"
          type="button"
          display="flex"
          alignItems="center"
          gap={2}
          width="100%"
          px={3}
          py={2}
          border="1px dashed"
          borderColor="base.border"
          borderRadius="md"
          color="text.secondary"
          cursor="pointer"
          transitionProperty="background-color, border-color"
          transitionDuration="fast"
          transitionTimingFunction="easeOutQuad"
          _hover={{
            bg: "base.lightBackground",
            borderColor: "base.borderHover",
          }}
          disabled={isBusy}
          _disabled={{
            color: "text.disabled",
            borderColor: "base.borderDisabled",
            cursor: "not-allowed",
            bg: "transparent",
          }}
          onClick={() => setIsCreating(true)}
        >
          <Box as={PlusIcon} boxSize={4} flexShrink={0} />
          <Text size="sm" color="inherit" fontWeight="medium">
            Create new folder
          </Text>
        </Box>
      )}
    </Column>
  );
};

const FolderRow = ({
  id,
  label,
  count,
  isSelected: isPicked,
  busyFolderId,
  onClick,
}: {
  id?: string;
  label: string;
  count?: number;
  isSelected: boolean;
  busyFolderId?: string;
  onClick: () => void;
}) => {
  const isBusy = busyFolderId !== undefined && busyFolderId === id;
  // The folder being moved to reads as picked while the move runs.
  const isSelected = isPicked || isBusy;
  return (
    <Box
      as="button"
      type="button"
      aria-pressed={isSelected}
      aria-busy={isBusy || undefined}
      disabled={busyFolderId !== undefined && !isBusy}
      _disabled={{
        color: "text.disabled",
        cursor: "not-allowed",
        bg: "transparent",
      }}
      display="flex"
      alignItems="center"
      gap={3}
      width="100%"
      px={3}
      py={2}
      textAlign="left"
      borderRadius="md"
      border="1px solid"
      borderColor={isSelected ? "primary.border" : "transparent"}
      bg={isSelected ? "primary.background" : "transparent"}
      color={isSelected ? "primary.text" : "text.primary"}
      cursor="pointer"
      transitionProperty="background-color, border-color"
      transitionDuration="fast"
      transitionTimingFunction="easeOutQuad"
      _hover={{
        bg: isSelected ? "primary.background" : "base.lightBackground",
      }}
      onClick={isBusy ? undefined : onClick}
    >
      {isBusy ? (
        <Box display="flex" flexShrink={0}>
          <Spinner size="sm" />
        </Box>
      ) : (
        <Box
          as={FolderIcon}
          boxSize={4}
          flexShrink={0}
          color={isSelected ? "primary.base" : "text.secondary"}
        />
      )}
      <Box flex={1} minWidth={0}>
        <Text size="sm" color="inherit" fontWeight="medium" isTruncated>
          {label}
        </Text>
      </Box>
      {count != null && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          minWidth={6}
          height={5}
          px={2}
          borderRadius="full"
          bg="base.lightBackground"
        >
          <Text size="sm" color="text.secondary">
            {count}
          </Text>
        </Box>
      )}
    </Box>
  );
};
